import { useSyncExternalStore } from "react";
import { lerLocal, gravarLocal } from "@/lib/armazenamento-local";
import type { MenuPreference } from "@/lib/types";

/**
 * TUT-465 — o JEITO DO MENU lateral, por pessoa. TUT-521 — dois jeitos, desde
 * que o dono tirou o "Menu híbrido" (23/10: "deixa so o recolhido e o
 * expandido"):
 *
 * - `texto` (o padrão): ícone + nome, sempre aberto — o expandido;
 * - `icones`: só ícones, com o nome no balão — o recolhido.
 *
 * Fica lembrado o ÚLTIMO jeito que a pessoa deixou, sem prazo de validade.
 * Mesmo desenho do tema (`useThemePreference`): mini-store de módulo, o
 * localStorage é cache da PRIMEIRA pintura, e a verdade durável mora no perfil
 * do usuário e chega com a sessão via `adoptMenuPreference()`.
 */
const STORAGE_KEY = "tutelae-menu";

/**
 * Tudo o que não for o recolhido vira o expandido: nada guardado, lixo, e o
 * `hover` do híbrido que um navegador ou uma sessão em cache ainda traga.
 */
function normalizar(valor: string | null | undefined): MenuPreference {
  return valor === "icones" ? "icones" : "texto";
}

function ler(): MenuPreference {
  try {
    return normalizar(lerLocal(STORAGE_KEY));
  } catch {
    return "texto";
  }
}

let jeito: MenuPreference = ler();
const ouvintes = new Set<() => void>();

/** Adota um jeito sem gravar no servidor: o caminho da sessão chegando. */
export function adoptMenuPreference(proximo: MenuPreference) {
  const normalizado = normalizar(proximo);
  try {
    // Grava mesmo sem mudança: um `hover` antigo no navegador some na primeira
    // sessão que chega.
    gravarLocal(STORAGE_KEY, normalizado);
  } catch {
    // O menu continua funcionando quando o navegador bloqueia armazenamento.
  }
  if (normalizado === jeito) return;
  jeito = normalizado;
  for (const ouvinte of ouvintes) ouvinte();
}

function assinar(ouvinte: () => void) {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

export function useMenuPreference() {
  return { jeito: useSyncExternalStore(assinar, () => jeito) };
}

/** Só para testes: volta a store ao estado de primeira carga. */
export function resetMenuPreferenceForTests() {
  jeito = ler();
}
